import type { Task, BoardTaskViewsData } from '../../types';
import type { ScheduledTask } from '../../stores/types';
import type { EmployeeStats, DoubleBookingWarning } from '../scheduler';
import { ScheduleCache as ScheduleCacheInstance } from '../scheduler/scheduleCache';
import { getEmployeesArray } from '../../employees/employeesStore';
import { BOARD_START_DATE } from '../../lib/dateUtils';
import { globalTasksStore } from '../../stores/globalTasksStore';
import { getBoardTaskViewStore, initBoardTaskViewStore } from './boardTaskViewStore';

// ============================================
// Per-Board Schedule Cache (using ScheduleCache class)
// ============================================

type ScheduleCacheData = {
  scheduledTasks: Map<string, ScheduledTask>;
  totalDays: number;
  employeeStats: Map<string, EmployeeStats>;
  doubleBookingWarnings: DoubleBookingWarning[];
  lastComputedAt: number;
} | null;

const scheduleCacheInstances = new Map<string, ScheduleCacheInstance>();

function getOrCreateCacheInstance(boardId: string): ScheduleCacheInstance {
  let cache = scheduleCacheInstances.get(boardId);
  if (!cache) {
    cache = new ScheduleCacheInstance();
    scheduleCacheInstances.set(boardId, cache);
  }
  return cache;
}

// Lazy import to avoid circular dependency
function getActiveBoardIdForStore(): string | null {
  try {
    const { getActiveBoardId } = require('../../stores/boardsStore');
    return getActiveBoardId();
  } catch {
    return null;
  }
}

function getBoardStartDateForStore(boardId: string): string {
  try {
    const { getBoardById } = require('../../stores/boardsStore');
    const board = getBoardById(boardId);
    return board?.startDate ?? BOARD_START_DATE;
  } catch {
    return BOARD_START_DATE;
  }
}

// Public API for schedule cache - backward compatible (uses active board)
export function getScheduleCache(boardId?: string): ScheduleCacheData {
  const id = boardId ?? getActiveBoardIdForStore();
  if (!id) return null;
  const cache = scheduleCacheInstances.get(id);
  if (!cache) return null;
  const snapshot = cache.getSnapshot();
  if (snapshot.lastComputedAt === 0) return null;
  return snapshot;
}

export function subscribeToScheduleCache(listener: () => void, boardId?: string): () => void {
  const id = boardId ?? getActiveBoardIdForStore();
  if (!id) return () => {};
  const cache = getOrCreateCacheInstance(id);
  return cache.subscribe(listener);
}

// ============================================
// Materialize: Combine GlobalTasks + BoardViews -> Task[]
// ============================================

export function materializeTasks(boardId: string): Task[] {
  const global = globalTasksStore.getSnapshot().context;
  let views: BoardTaskViewsData;
  try {
    views = getBoardTaskViewStore(boardId).getSnapshot().context.views;
  } catch {
    // Store not initialized yet
    return [];
  }

  const tasks: Task[] = [];
  for (const [taskId, view] of Object.entries(views)) {
    const globalTask = global.tasksById[taskId];
    if (!globalTask) continue; // Ghost view entry, skip
    tasks.push({
      ...globalTask,
      column: view.column,
    });
  }
  return tasks;
}

// ============================================
// Public API
// ============================================

export function getTasksArray(boardId?: string): Task[] {
  const id = boardId ?? getActiveBoardIdForStore();
  if (!id) return [];
  return materializeTasks(id);
}

// ============================================
// Board Initialization (schedule computation)
// ============================================

export function initBoardSchedule(boardId: string, boardStartDate: string = BOARD_START_DATE): void {
  // Ensure boardTaskViewStore is initialized
  initBoardTaskViewStore(boardId);

  const tasks = materializeTasks(boardId);
  const employees = getEmployeesArray();
  const cache = getOrCreateCacheInstance(boardId);
  cache.compute(tasks, employees, boardStartDate);
}

// ============================================
// Manual Schedule Recalculation
// ============================================

export function recalculateSchedule(boardId?: string): void {
  const id = boardId ?? getActiveBoardIdForStore();
  if (!id) return;

  const boardStartDate = getBoardStartDateForStore(id);
  const tasks = materializeTasks(id);
  const employees = getEmployeesArray();
  const cache = getOrCreateCacheInstance(id);
  cache.compute(tasks, employees, boardStartDate);
}

export function disposeScheduleCache(boardId: string): void {
  const cache = scheduleCacheInstances.get(boardId);
  if (cache) {
    cache.dispose();
    scheduleCacheInstances.delete(boardId);
  }
}
